import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Upload, Sparkles, Save, X, RefreshCw, Trophy } from "lucide-react";
import useModalA11y from "@/lib/useModalA11y";

const SPORTS = [
  { key: "football", label: "Football", emoji: "⚽", color: "#22C55E" },
  { key: "rugby", label: "Rugby", emoji: "🏉", color: "#8B5CF6" },
  { key: "tennis", label: "Tennis", emoji: "🎾", color: "#EAB308" },
  { key: "running", label: "Running", emoji: "🏃", color: "#EF4444" },
  { key: "gym", label: "Gym", emoji: "🏋️", color: "#f47a20" },
  { key: "cycling", label: "Cycling", emoji: "🚴", color: "#3B82F6" },
  { key: "swim", label: "Swim", emoji: "🏊", color: "#06B6D4" },
  { key: "basketball", label: "Basketball", emoji: "🏀", color: "#F97316" },
];

async function renderFrameCanvas(sourceDataUrl, sport, displayName) {
  const sportDef = SPORTS.find((s) => s.key === sport) || SPORTS[0];
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const W = 480, H = 640;
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");

      // Background gradient
      const grad = ctx.createLinearGradient(0, 0, W, H);
      grad.addColorStop(0, sportDef.color);
      grad.addColorStop(1, "#0A0A0A");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // Inner dark plate
      ctx.fillStyle = "#0A0A0A";
      ctx.fillRect(24, 24, W - 48, H - 48);

      // Draw the photo (contain into a square area)
      const box = { x: 40, y: 90, w: W - 80, h: 400 };
      const ratio = Math.max(box.w / img.width, box.h / img.height);
      const iw = img.width * ratio;
      const ih = img.height * ratio;
      ctx.save();
      ctx.beginPath();
      ctx.rect(box.x, box.y, box.w, box.h);
      ctx.clip();
      ctx.drawImage(img, box.x + (box.w - iw) / 2, box.y + (box.h - ih) / 2, iw, ih);
      ctx.restore();

      // Sport emoji badge (top-right)
      ctx.font = "48px serif";
      ctx.fillText(sportDef.emoji, W - 88, 68);

      // Header
      ctx.fillStyle = sportDef.color;
      ctx.font = "700 12px 'Barlow Condensed', sans-serif";
      ctx.fillText("FUNCTIONAL LAB · SPORTS CARD", 40, 60);

      // Name
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "800 34px 'Barlow Condensed', sans-serif";
      ctx.textTransform = "uppercase";
      ctx.fillText((displayName || "ATHLETE").toUpperCase(), 40, 540);

      // Role bar
      ctx.fillStyle = sportDef.color;
      ctx.fillRect(40, 555, 120, 3);

      ctx.fillStyle = "rgba(255,255,255,0.7)";
      ctx.font = "600 13px 'DM Sans', sans-serif";
      ctx.fillText(`${sportDef.label.toUpperCase()} · MOVEMENT IS MEDICINE`, 40, 588);

      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = sourceDataUrl;
  });
}

export default function StickerStudio({ onClose }) {
  const [source, setSource] = useState(null);
  const [sport, setSport] = useState("football");
  const [name, setName] = useState("");
  const [preview, setPreview] = useState(null);
  const [style, setStyle] = useState("frame"); // frame | ai
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  // Load existing sticker if any (to prefill name/sport)
  useEffect(() => {
    api.get("/patient/sticker/mine").then((r) => {
      if (r.data) {
        setName(r.data.display_name || "");
        setSport(r.data.sport || "football");
        setStyle(r.data.style || "frame");
        setPreview(r.data.image_data_url);
      }
    }).catch(() => {});
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please pick an image file");
    if (f.size > 8 * 1024 * 1024) return toast.error("Image must be < 8 MB");
    const reader = new FileReader();
    reader.onload = () => setSource(reader.result);
    reader.readAsDataURL(f);
    setPreview(null);
  };

  const genFrame = async () => {
    if (!source) return toast.error("Upload a photo first");
    setBusy(true);
    try {
      const out = await renderFrameCanvas(source, sport, name);
      setPreview(out);
      setStyle("frame");
    } catch { toast.error("Could not render frame"); }
    setBusy(false);
  };

  const genAI = async () => {
    if (!source) return toast.error("Upload a photo first");
    setBusy(true);
    try {
      const { data } = await api.post("/patient/sticker/ai", {
        image_data_url: source,
        sport,
        display_name: name || null,
      });
      setPreview(data.image_data_url);
      setStyle("ai");
      toast.success("AI sticker ready!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed");
    }
    setBusy(false);
  };

  const save = async () => {
    if (!preview) return toast.error("Generate a sticker first");
    setBusy(true);
    try {
      await api.post("/patient/sticker/save", {
        image_data_url: preview,
        sport,
        display_name: name || null,
        style,
      });
      toast.success("Sticker saved as your avatar");
      onClose?.();
    } catch { toast.error("Could not save"); }
    setBusy(false);
  };

  const dialogRef = useModalA11y({ open: true, onClose: () => onClose?.(), disableEscape: busy });

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto p-4 flex items-start md:items-center justify-center" data-testid="sticker-studio">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sticker-studio-title"
        className="fl-card w-full max-w-4xl relative"
      >
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--fl-border)" }}>
          <div>
            <div className="fl-label text-[color:var(--fl-orange)]">Sticker studio</div>
            <div id="sticker-studio-title" className="fl-heading text-xl mt-1">Make your Panini-style athlete card</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="sticker-close" aria-label="Close sticker studio"><X size={20} /></button>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-6">
          {/* Left: inputs */}
          <div className="space-y-4">
            <div>
              <div className="fl-label mb-2">1 · Your photo</div>
              <div className="flex gap-2">
                <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={onFile} className="hidden" data-testid="sticker-file-input" />
                <button className="fl-btn-ghost text-sm" onClick={() => fileRef.current?.click()} data-testid="sticker-upload">
                  <Upload size={14} /> {source ? "Change photo" : "Upload / take photo"}
                </button>
              </div>
              {source && (
                <img src={source} alt="source" className="mt-3 w-24 h-24 object-cover rounded-md" style={{ border: "1px solid var(--fl-border)" }} data-testid="sticker-source-preview" />
              )}
            </div>

            <div>
              <div className="fl-label mb-2">2 · Sport</div>
              <div className="grid grid-cols-4 gap-2">
                {SPORTS.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSport(s.key)}
                    className={`fl-card p-3 text-center transition-all ${sport === s.key ? "" : "opacity-60"}`}
                    style={sport === s.key ? { borderColor: s.color, borderWidth: 2 } : {}}
                    data-testid={`sticker-sport-${s.key}`}
                  >
                    <div className="text-2xl">{s.emoji}</div>
                    <div className="text-[10px] mt-1 fl-label">{s.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="fl-label mb-2">3 · Name on the card</div>
              <input
                className="fl-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your athlete name"
                data-testid="sticker-name-input"
              />
            </div>

            <div>
              <div className="fl-label mb-2">4 · Choose your style</div>
              <div className="flex flex-wrap gap-2">
                <button className="fl-btn-ghost text-sm" onClick={genFrame} disabled={busy || !source} data-testid="sticker-gen-frame">
                  <Trophy size={14} /> Classic frame
                </button>
                <button className="fl-btn text-sm" onClick={genAI} disabled={busy || !source} data-testid="sticker-gen-ai">
                  <Sparkles size={14} /> {busy && style !== "frame" ? "Generating…" : "AI player sticker"}
                </button>
              </div>
              <div className="text-[11px] mt-2" style={{ color: "var(--fl-muted-2)" }}>
                Classic = fast, drawn locally. AI = re-renders you as a stylised sports player (takes ~10s).
              </div>
            </div>
          </div>

          {/* Right: preview */}
          <div>
            <div className="fl-label mb-2">Preview</div>
            <div className="fl-card p-3 flex items-center justify-center min-h-[420px] relative overflow-hidden">
              {preview ? (
                <img src={preview} alt="sticker" className="max-h-[500px] w-auto rounded-md" data-testid="sticker-preview" />
              ) : (
                <div className="text-center text-sm" style={{ color: "var(--fl-muted-2)" }}>
                  {source ? "Choose a style to generate your sticker." : "Upload a photo to get started."}
                </div>
              )}
              {busy && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-md">
                  <RefreshCw size={20} className="animate-spin text-[color:var(--fl-orange)]" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button className="fl-btn-ghost text-sm" onClick={onClose}>Cancel</button>
              <button className="fl-btn text-sm" onClick={save} disabled={busy || !preview} data-testid="sticker-save">
                <Save size={14} /> Set as my avatar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
